import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/api/client';
import { parsePaginatedResponse, getApiErrorMessage } from '@/api/types';
import { setToast } from '../slices/toastSlice';
import { setTransfer, setEntree, setSortie, setVente } from '../slices/movementSlice';
import type { MovementRecord } from '../slices/movementSlice';

const PAGE_SIZE = 10;

export const fetchTransfers = createAsyncThunk<void, number | undefined>(
  'movement/fetchTransfers',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/citernes/transfers', { params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' } });
        const { list, pageNum, totalPages } = parsePaginatedResponse<MovementRecord>(data);
        dispatch(setTransfer({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: MovementRecord[] }>('/citernes/transfers');
        dispatch(setTransfer({ data: Array.isArray(data?.data) ? data.data : [], pageNum: 0, totalPages: 0 }));
      }
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Erreur');
    }
  }
);

export const fetchEntrees = createAsyncThunk<void, number | undefined>(
  'movement/fetchEntrees',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/citernes/entrees', { params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' } });
        const { list, pageNum, totalPages } = parsePaginatedResponse<MovementRecord>(data);
        dispatch(setEntree({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: MovementRecord[] }>('/citernes/entrees');
        dispatch(setEntree({ data: Array.isArray(data?.data) ? data.data : [], pageNum: 0, totalPages: 0 }));
      }
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Erreur');
    }
  }
);

export const fetchSorties = createAsyncThunk<void, number | undefined>(
  'movement/fetchSorties',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/citernes/sorties', { params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' } });
        const { list, pageNum, totalPages } = parsePaginatedResponse<MovementRecord>(data);
        dispatch(setSortie({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: MovementRecord[] }>('/citernes/sorties');
        dispatch(setSortie({ data: Array.isArray(data?.data) ? data.data : [], pageNum: 0, totalPages: 0 }));
      }
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Erreur');
    }
  }
);

export const fetchVentes = createAsyncThunk<void, number | undefined>(
  'movement/fetchVentes',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/citernes/ventes', { params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' } });
        const { list, pageNum, totalPages } = parsePaginatedResponse<MovementRecord>(data);
        dispatch(setVente({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: MovementRecord[] }>('/citernes/ventes');
        dispatch(setVente({ data: Array.isArray(data?.data) ? data.data : [], pageNum: 0, totalPages: 0 }));
      }
    } catch (err: unknown) {
      return rejectWithValue(err instanceof Error ? err.message : 'Erreur');
    }
  }
);

// Transfer CRUD
export const createTransfer = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/createTransfer',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/citernes/transfers', payload);
      dispatch(setToast({ message: 'Transfert créé', severity: 'success' }));
      await dispatch(fetchTransfers(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const updateTransfer = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/updateTransfer',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/citernes/transfers/${payload.id}`, payload);
      dispatch(setToast({ message: 'Transfert modifié', severity: 'success' }));
      await dispatch(fetchTransfers(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const deleteTransfer = createAsyncThunk<boolean, number, { rejectValue: string }>(
  'movement/deleteTransfer',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/citernes/transfers/${id}`);
      dispatch(setToast({ message: 'Transfert supprimé', severity: 'success' }));
      await dispatch(fetchTransfers(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);

// Entree CRUD
export const createEntree = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/createEntree',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/citernes/entrees', payload);
      dispatch(setToast({ message: 'Entrée créée', severity: 'success' }));
      await dispatch(fetchEntrees(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const updateEntree = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/updateEntree',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/citernes/entrees/${payload.id}`, payload);
      dispatch(setToast({ message: 'Entrée modifiée', severity: 'success' }));
      await dispatch(fetchEntrees(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const deleteEntree = createAsyncThunk<boolean, number, { rejectValue: string }>(
  'movement/deleteEntree',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/citernes/entrees/${id}`);
      dispatch(setToast({ message: 'Entrée supprimée', severity: 'success' }));
      await dispatch(fetchEntrees(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);

// Sortie CRUD
export const createSortie = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/createSortie',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/citernes/sorties', payload);
      dispatch(setToast({ message: 'Sortie créée', severity: 'success' }));
      await dispatch(fetchSorties(0));
      return true;
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err);
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const updateSortie = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/updateSortie',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/citernes/sorties/${payload.id}`, payload);
      dispatch(setToast({ message: 'Sortie modifiée', severity: 'success' }));
      await dispatch(fetchSorties(0));
      return true;
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err);
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const deleteSortie = createAsyncThunk<boolean, number, { rejectValue: string }>(
  'movement/deleteSortie',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/citernes/sorties/${id}`);
      dispatch(setToast({ message: 'Sortie supprimée', severity: 'success' }));
      await dispatch(fetchSorties(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);

// Vente CRUD
export const createVente = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/createVente',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/citernes/ventes', payload);
      dispatch(setToast({ message: 'Vente créée', severity: 'success' }));
      await dispatch(fetchVentes(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const updateVente = createAsyncThunk<boolean, MovementRecord, { rejectValue: string }>(
  'movement/updateVente',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/citernes/ventes/${payload.id}`, payload);
      dispatch(setToast({ message: 'Vente modifiée', severity: 'success' }));
      await dispatch(fetchVentes(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
export const deleteVente = createAsyncThunk<boolean, number, { rejectValue: string }>(
  'movement/deleteVente',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/citernes/ventes/${id}`);
      dispatch(setToast({ message: 'Vente supprimée', severity: 'success' }));
      await dispatch(fetchVentes(0));
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur';
      dispatch(setToast({ message: msg, severity: 'error' }));
      return rejectWithValue(msg);
    }
  }
);
